import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  CoreQuestionsFactoryService,
  FormQuestionsService,
  CountriesService,
  AgentsCacheService,
  ProfileService,
  CustomizationsService,
  CoursesService,
  UtilsQuestionsService
} from 'core/services';
import {
  QuestionBase,
  TextboxQuestion,
  QuestionGroup,
  DropdownQuestion,
  TagsQuestion,
  HiddenQuestion
} from 'core/components/forms';
import { UntypedFormGroup } from '@angular/forms';
import { environment } from 'environments/environment';

@Injectable()
export class DefaultDataQuestionsService implements FormQuestionsService {
  protected questions: QuestionBase<any>[];
  protected groupDataQuestionGroup: QuestionGroup;
  protected agentsQuestionGroup: QuestionGroup;
  protected questionsSubject = new BehaviorSubject<any>(null);
  protected formData: any = {};
  protected timezoneSubject = new BehaviorSubject<any>(null);
  protected minDateSubject = new BehaviorSubject<any>(null);
  protected maxDateSubject = new BehaviorSubject<any>(null);
  protected form: UntypedFormGroup;
  protected gameChange: Subject<any>;
  protected userRole: any;
  protected roleName: any;

  constructor(
    protected factory: CoreQuestionsFactoryService,
    protected countries: CountriesService,
    protected customizationsService: CustomizationsService,
    protected translateService: TranslateService,
    protected profileService: ProfileService,
    protected agentsCache: AgentsCacheService,
    protected coursesService: CoursesService,
    protected utilsQuestionsService: UtilsQuestionsService
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => {
      this.userRole = userRole;
      this.roleName = userRole.name;
    });
  }

  setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  getFormData(): any {
    return null;
  }

  onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  setGameChangeSubject(gameChange: Subject<any>) {
    this.gameChange = gameChange;
  }

  async onClientSelected(client, params) {
    this.agentsCache.updateClientAgents(client.id);
    this.agentsCache.updateDistributorAgents(client.distributorId);
    // await this.coursesService.loadClientCatalogueCache(client.id);

    // Set the course question
    // const { childQuestions } = this.groupDataQuestionGroup;
    // const courseQuestionIndex = this.utilsQuestionsService.findQuestionIndex('customizationId', childQuestions);
    // const newCourseQuestion = this.getCourseQuestion({ ...params, client });
    // childQuestions[courseQuestionIndex] = newCourseQuestion;

    params.client = client;
    this.setFormClient(client.countryId, params);
  }

  /**
   * Set the selected country, timezone and bonus that depends of client
   * @param country key
   * @param params data
   */
  private setFormClient(country: string, params: any): void {
    const { childQuestions } = this.groupDataQuestionGroup;

    // Set the country dropdown
    const countryQuestionIndex = this.utilsQuestionsService.findQuestionIndex('country', childQuestions);
    const newCountryQuestion = this.getCountryQuestion(params, country, true);
    childQuestions[countryQuestionIndex] = newCountryQuestion;

    // Set the timezone dropdown
    const timezone = this.countries.getDefaultTimezone(country);
    const questionIndex = this.utilsQuestionsService.findQuestionIndex('timezone', childQuestions);
    const newTimezoneQuestion = this.getTimezoneQuestion(country, timezone, true);
    childQuestions[questionIndex] = newTimezoneQuestion;
    this.form.markAsPristine();
  }

  getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  getFormQuestions(params: any = {}): Observable<QuestionBase<any>[]> {
    const { group } = params;
    this.questions = [];
    this.onDataChanged();
    this.minDateSubject.next(group?.startDate ? new Date(group.startDate) : null);
    this.maxDateSubject.next(group?.endDate ? new Date(group.endDate) : null);
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  createQuestions(params: any = {}) {}

  getGroupDataQuestions(params: any): QuestionBase<any> {
    this.groupDataQuestionGroup = new QuestionGroup({
      title: 'mars.info',
      isSection: true,
      childQuestions: this.createGroupDataQuestions(params).filter((question) => question != null)
    });
    return this.groupDataQuestionGroup;
  }

  createGroupDataQuestions(params: any = {}): QuestionBase<any>[] {
    const { group } = params;
    const client = !params.client && group ? { id: group.clientId, name: group.clientName } : params.client;
    const country = group?.country || client?.countryId || null;
    const timezone = group?.timezone || client?.timezone || null;
    const editable = !params.client?.scorm?.enabled;
    Object.assign(params, { client });
    return [
      // todo delete when customizationId is not required in mars backend
      new HiddenQuestion({
        key: 'customizationId',
        value: group ? group.customizationId : environment.MARS_ID
      }),
      this.getClientQuestion(params, country, timezone),
      new TextboxQuestion({
        key: 'name',
        label: 'nouns.name',
        class: 'col-1',
        editable,
        value: group?.name || this.form?.controls['name']?.value || this.formData.name,
        validators: { required: true }
      }),
      this.getTopicQuestion(params, editable),
      this.getCountryQuestion(params, country, editable),
      this.getTimezoneQuestion(country, timezone, editable),
      // this.getNotificationsConfig(params),
      this.getTagsQuestion(params, editable)
    ];
  }

  getClientQuestion(params, country, timezone): QuestionBase<any> {
    const { group, client } = params;
    return this.factory.getClientQuestion({
      ...params,
      class: 'col-1',
      validators: { required: true },
      editable: !client,
      disabled: group,
      entityOptions: { filters: { 'productivityCfg.mars.enabled': 'true' } },
      childQuestions: group
        ? []
        : [
            {
              key: 'country',
              value: country,
              source: 'countryId'
            },
            {
              key: 'timezone',
              value: timezone,
              source: 'timezone'
            }
          ],
      eventHandlers: {
        onSelected: async (clientSelected) => await this.onClientSelected(clientSelected, params)
      }
    });
  }

  getTopicQuestion(params, editable) {
    const { group } = params;

    return new TextboxQuestion({
      key: 'topic',
      label: 'nouns.topic',
      class: 'col-2-offset',
      value: group?.topic || this.formData.topic,
      editable,
      validators: { required: false }
    });
  }

  getCountryQuestion(params, country, editable) {
    return this.factory.getCountryQuestion({
      ...params,
      class: 'col-2',
      value: country,
      validators: { required: true },
      editable,
      eventHandlers: {
        onChange: (value) => this.setFormClient(value, params)
      }
    });
  }

  getTimezoneQuestion(country, timezone, editable) {
    this.timezoneSubject.next(timezone);
    return this.factory.getTimezoneQuestion({
      country,
      class: 'col-2',
      value: timezone,
      validators: { required: true },
      editable,
      eventHandlers: {
        onChange: (value) => {
          this.timezoneSubject.next(value);
        }
      }
    });
  }

  getTagsQuestion(params, editable): QuestionBase<any> {
    const { group } = params;

    return new TagsQuestion({
      key: 'tags',
      label: 'nouns.tags',
      class: 'col-1',
      editable,
      value: group?.tags || []
    });
  }

  // getNotificationsConfig(params, hasBonus?: false): QuestionBase<any> {
  //   const { group } = params;
  //   const notificationsCfg = group?.notificationsCfg || { n2: { enabled: true } };
  //   return new QuestionGroup({
  //     key: 'notificationsCfg',
  //     class: 'col-1',
  //     childQuestions: [
  //       new QuestionGroup({
  //         key: 'n2',
  //         childQuestions: [
  //           new CheckboxQuestion({
  //             key: 'enabled',
  //             tooltipKey: 'notificationsCfg.n2.enabled',
  //             class: 'col-1',
  //             label: 'notifications.config.startClass',
  //             value: notificationsCfg.n2.enabled,
  //             editable: !params?.client?.scorm?.enabled,
  //             disabled: group?.hasBonus || hasBonus
  //           })
  //         ]
  //       })
  //     ]
  //   });
  // }

  // getAgentsQuestionGroup(params, editable) {
  //   this.agentsQuestionGroup = new QuestionGroup({
  //     title: 'groups.agents',
  //     isSection: true,
  //     isDropdown: this.userRole.scope !== 'client',
  //     childQuestions: [this.getAgentsQuestions(params, editable)]
  //   });
  //   return this.agentsQuestionGroup;
  // }

  // getAgentsQuestions(params, editable) {
  //   const { group, client, hasBonus = false, agentIds = [] } = params;
  //   const clientId = group?.clientId || client?.id || null;
  //   const distributorId = group?.distributorId || client?.distributorId || null;
  //   const agentsList = group?.agentIds || agentIds;
  //   this.agentsCache.updateClientAgents(clientId);
  //   this.agentsCache.updateDistributorAgents(distributorId);

  //   return new QuestionGroup({
  //     key: 'agentIds',
  //     controlType: 'agents',
  //     value: agentsList,
  //     childQuestions: [
  //       this.factory.getAgentQuestion({
  //         key: 'cs',
  //         value: agentsList,
  //         label: 'nouns.cs',
  //         class: 'col-1',
  //         group,
  //         client,
  //         editable,
  //         hasBonus,
  //         role: ROLES.CS
  //       }),
  //       this.factory.getAgentQuestion({
  //         key: 'salesman',
  //         value: agentsList,
  //         label: 'nouns.salesmans',
  //         class: 'col-1',
  //         group,
  //         client,
  //         editable,
  //         hasBonus,
  //         role: ROLES.SALES
  //       }),
  //       this.factory.getAgentQuestion({
  //         key: 'distributors',
  //         value: agentsList,
  //         label: 'nouns.distributors',
  //         class: 'col-1',
  //         group,
  //         client,
  //         hasBonus,
  //         role: ROLES.DISTRIBUTOR,
  //         disabled: this.roleName === 'distributor',
  //         editable: this.roleName !== 'distributor' && editable
  //       }),
  //       this.getHrQuestion(params, hasBonus, editable)
  //     ].filter((question) => question !== null)
  //   });
  // }

  // getHrQuestion(params, hasBonus, editable) {
  //   const { group, client, agentIds = [] } = params;
  //   const clientId = group?.clientId || client?.id || null;
  //   const agentsList = group?.agentIds || agentIds;
  //   this.agentsCache.updateClientAgents(clientId);

  //   const question = this.factory.getAgentQuestion({
  //     key: 'hr',
  //     value: agentsList,
  //     label: 'nouns.hrname',
  //     class: `col-1`,
  //     group,
  //     client,
  //     disabled: this.roleName === 'distributor',
  //     editable: this.roleName !== 'distributor' && editable,
  //     required: hasBonus,
  //     validators: {
  //       required: hasBonus,
  //       function: (formControl: UntypedFormControl) => {
  //         const agentIds = formControl.value;
  //         const hasHrAgents = agentIds
  //           ? agentIds.some((id) => {
  //               const agent = this.agentsCache.getAgent(id);
  //               return agent && ['hr', 'clientAdmin', 'dachAdmin'].includes(agent.role);
  //             })
  //           : false;

  //         if (!hasBonus || hasHrAgents) {
  //           return null;
  //         }
  //         return {
  //           required: false
  //         };
  //       }
  //     },
  //     role: ROLES.HR
  //   });

  //   return question;
  // }
}
